'use client';

import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { cn } from '../../utils/cn';

export interface ScrollAreaProps extends React.HTMLAttributes<HTMLDivElement> {
  onScrollToBottom?: () => void;
  scrollToBottomThreshold?: number;
}

export const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(
  (
    { className, children, onScrollToBottom, scrollToBottomThreshold = 1, onScroll, ...props },
    ref
  ) => {
    const innerRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => innerRef.current as HTMLDivElement);

    useEffect(() => {
      const timeoutId = setTimeout(() => {
        if (onScrollToBottom && innerRef.current) {
          const element = innerRef.current;
          // Check if content is not scrollable (scrollHeight <= clientHeight)
          if (element.scrollHeight <= element.clientHeight) {
            onScrollToBottom();
          }
        }
      }, 500);

      return () => clearTimeout(timeoutId);
    }, [onScrollToBottom, children]);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
      if (onScroll) {
        onScroll(e);
      }

      if (onScrollToBottom) {
        const target = e.target as HTMLElement;
        if (
          target.scrollHeight - target.scrollTop - target.clientHeight <
          scrollToBottomThreshold
        ) {
          onScrollToBottom();
        }
      }
    };

    return (
      <div
        ref={innerRef}
        className={cn('overflow-y-auto', className)}
        onScroll={handleScroll}
        {...props}
      >
        {children}
      </div>
    );
  }
);

ScrollArea.displayName = 'ScrollArea';
